<script lang="ts" setup>
import { ref, inject, onMounted, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import axiosInstance from '@/plugins/axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useToast } from 'primevue';
import type { FileUploadSelectEvent } from 'primevue/fileupload';
import SettingsPageTemplate from '@/components/templates/SettingsPageTemplate.vue';
import DynamicSettingsForm from '@/components/forms/DynamicSettingsForm.vue';
import type { DynamicSettingsField } from '@/components/forms/DynamicSettingsForm.vue';

interface ModuleOption {
    key: string;
    name: string;
}

const route = useRoute();
const router = useRouter();
const gF = inject('gF') as any;
const isSaving = ref(false);
const loading = ref(false);
const assetsUrl = ref(import.meta.env.VITE_ADMIN_ASSETS_URL);
const src = ref<string | null>(null);
const toast = useToast();
const initialKeySet = ref(false);
const moduleOptions = ref<ModuleOption[]>([]);
const moduleSelectOptions = computed(() =>
    [{ key: '', name: 'None', value: '' }, ...moduleOptions.value.map(m => ({ ...m, value: m.key }))]
);

const categoryList = ref([
    { name: 'Main Question Box', value: 'Main Question Box' },
    { name: 'Main Page Appearance', value: 'Main Page Appearance' },
    { name: 'Infoboxes', value: 'Infoboxes' },
    { name: 'Result Page', value: 'Result Page' },
    { name: 'Imprint and Data Privacy', value: 'Imprint and Data Privacy' },
    { name: 'Result PDF', value: 'Result PDF' },
    { name: 'Main Page Texts', value: 'Main Page Texts' },
    { name: 'FAQ', value: 'FAQ' },
    { name: 'Social Media', value: 'Social Media' },
    { name: 'SEO', value: 'SEO' },
    { name: 'Lead Generation', value: 'Lead Generation' },
    { name: 'Misc', value: 'Misc' }
]);

const fieldTypeList = ref([
    { name: 'Color', value: 'color' },
    { name: 'Font', value: 'font' },
    { name: 'Link', value: 'link' },
    { name: 'Checkbox', value: 'checkbox' },
    { name: 'File', value: 'file' },
    { name: 'Text', value: 'text' },
    { name: 'Translation', value: 'translation' },
    { name: 'Predefined Select Dropdown', value: 'predefinedSelect' },
]);

const extendedView = ref(false);
const optionDetails = ref<any>(null);
const optionId = route.params.id as string;
const isNew = optionId === 'new';

const settingsFields = computed<DynamicSettingsField[]>(() => {
    const fields: DynamicSettingsField[] = [];
    const fieldType = optionDetails.value?.fieldType || '';

    if (extendedView.value) {
        fields.push({ key: 'key', label: 'Key', type: 'text', disabled: !initialKeySet.value });
    }

    fields.push(
        { key: 'title', label: 'Title', type: 'text' },
        {
            key: 'category', label: 'Category', type: 'select',
            options: categoryList.value, optionLabel: 'name', optionValue: 'value',
            placeholder: 'Select a category'
        },
        {
            key: 'moduleKey', label: 'Module', type: 'select',
            options: moduleSelectOptions.value, optionLabel: 'name', optionValue: 'key',
            placeholder: 'Select a module (optional)'
        }
    );

    if (extendedView.value) {
        fields.push(
            { key: 'hidden', label: 'Hidden?', type: 'toggle' },
            {
                key: 'fieldType', label: 'Field Type', type: 'select',
                options: fieldTypeList.value, optionLabel: 'name', optionValue: 'value',
                placeholder: 'Select a type'
            }
        );

        if (!['file', 'text', 'translation'].includes(fieldType)) {
            fields.push(
                {
                    key: 'cssClass', label: 'CSS Classes', type: 'textarea', rows: 3,
                    placeholder: "Enter CSS classes (e.g., '.class1, .class2, #id2' or ['.class1', '#id2'])",
                    help: "You can specify multiple selectors either as comma-separated values or as a JavaScript-style array."
                },
                { key: 'cssType', label: 'CSS Property', type: 'text', placeholder: 'Enter a CSS property' }
            );
        }
    }

    fields.push({ key: 'help', label: 'Help Text', type: 'textarea' });

    return fields;
});

const onFileSelect = (event: FileUploadSelectEvent) => {
    const file = event.files[0];
    const reader = new FileReader();

    reader.onload = async (e) => {
        src.value = (e.target?.result as string) ?? null;

        const formData = new FormData();
        formData.append("file", file);

        try {
            const res = await axiosInstance.post(`/admin/customizationOptions/${optionId}/uploadFile`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            optionDetails.value.helpImage = res.data.path;
        } catch (error) {
            console.error("Upload failed:", error);
        }
    };

    reader.readAsDataURL(file);
};

const saveOption = async () => {
    isSaving.value = true;

    try {
        if (isNew) {
            await axiosInstance.post(`/admin/customizationOptions`, optionDetails.value);
        } else {
            await axiosInstance.put(`/admin/customizationOptions/${optionId}`, optionDetails.value);
        }

        toast.add({
            severity: 'success',
            life: 3000,
            summary: 'Success',
            detail: `Customization option ${isNew ? 'created' : 'saved'} successfully!`,
        });

        if (isNew) {
            router.push({ name: 'CustomizationOverview' });
        }
    } catch (error) {
        toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to save customization option.' });
    } finally {
        isSaving.value = false;
    }
};

onMounted(async () => {
    gF.startLoading();
    loading.value = true;

    try {
        const modulesRes = await axiosInstance.get('/modules');
        moduleOptions.value = (modulesRes.data || []).map((m: any) => ({ key: m.key, name: m.name || m.key }));
    } catch (e) {
        console.error('Failed to load modules', e);
    }

    if (isNew) {
        extendedView.value = true;
        initialKeySet.value = true;
        optionDetails.value = {
            key: '', title: '', category: '', fieldType: '',
            options: [], cssClass: '', cssType: '', help: '', helpImage: '', moduleKey: '',
        };
    } else {
        const response = await axiosInstance.get(`/admin/customizationOptions/${optionId}`);
        optionDetails.value = { ...response.data, moduleKey: response.data?.moduleKey || '' };
    }

    loading.value = false;
    gF.endLoading();
});
</script>

<template>
    <SettingsPageTemplate
        :loading="isSaving"
        :saveDisabled="isSaving || loading"
        @save="saveOption"
    >
        <template #actions-left>
            <ToggleButton v-model="extendedView" onLabel="Extended View" offLabel="Normal View" />
        </template>

        <template #settings-content>
            <DynamicSettingsForm
                v-if="optionDetails"
                v-model="optionDetails"
                :fields="settingsFields"
                :disabled="isSaving || loading"
                :columns="2"
            />

            <!-- Available Options editor — only for predefinedSelect field type -->
            <div v-if="extendedView && optionDetails?.fieldType === 'predefinedSelect'" class="field-wrapper">
                <label class="field-label">Available Options</label>
                <div v-for="(option, index) in optionDetails.options" :key="index" class="mb-2">
                    <div class="d-flex align-items-center gap-2">
                        <InputText v-model="option.name" placeholder="Name" class="form-control me-2" />
                        <InputText v-model="option.value" placeholder="Value" class="form-control me-2" />
                        <Button severity="danger" @click="optionDetails.options.splice(index, 1)">
                            <template #icon>
                                <FontAwesomeIcon :icon="['fas', 'trash']" />
                            </template>
                        </Button>
                    </div>
                </div>
                <Button label="Add Option" class="p-button-tuev mt-2"
                    @click="optionDetails.options.push({ name: '', value: '' })">
                    <template #icon>
                        <FontAwesomeIcon :icon="['fas', 'plus']" />
                    </template>
                </Button>
            </div>

            <!-- Help Image upload -->
            <div v-if="optionDetails" class="field-wrapper">
                <label class="field-label">Help Image</label>
                <FileUpload mode="advanced" name="helpImage" :customUpload="true" accept="image/*"
                    :maxFileSize="1000000" :multiple="false" :auto="false" @select="onFileSelect"
                    :showUploadButton="false" :showCancelButton="false">
                    <template #header="{ chooseCallback }">
                        <div class="flex gap-2">
                            <Button @click="chooseCallback()" rounded outlined severity="secondary">
                                <template #icon>
                                    <FontAwesomeIcon :icon="['fas', 'folder-open']" />
                                </template>
                            </Button>
                            <Button @click="optionDetails.helpImage = null" rounded outlined
                                severity="danger" :disabled="!optionDetails.helpImage">
                                <template #icon>
                                    <FontAwesomeIcon :icon="['fas', 'trash']" />
                                </template>
                            </Button>
                        </div>
                    </template>

                    <template #content="{ files }">
                        <div v-if="files.length > 0" class="pt-4">
                            <div class="flex flex-wrap gap-4">
                                <div v-for="file in files" :key="file.name + file.type + file.size"
                                    class="flex flex-col items-center gap-2">
                                    <img :src="(file as any).objectURL" alt="preview" width="100" />
                                    <span class="text-sm">{{ file.name }}</span>
                                </div>
                            </div>
                        </div>
                    </template>

                    <template #empty>
                        <div class="flex flex-col items-center justify-center">
                            <i class="pi pi-cloud-upload text-4xl text-muted-color mb-2" />
                            <Image v-if="optionDetails.helpImage"
                                :src="assetsUrl + optionDetails.helpImage" alt="Help Image" width="200" preview />
                        </div>
                    </template>
                </FileUpload>
            </div>
        </template>
    </SettingsPageTemplate>
</template>

<style scoped>
.p-fileupload {
    width: 100%;
}

.field-wrapper {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.field-label {
    display: block;
    font-weight: 600;
    color: var(--text-color, #111827);
}
</style>
